"use client";

import { useRouter } from "next/navigation";
import React from "react";
import Avatar from "@repo/ui/avatar";
import BrandedActionButton from "src/components/branded-action-button";
import { BodyText, HeadlineText } from "src/components/KinsaTypography";
import { Routes } from "src/lib/routes";
import { useButtonPendingAction } from "src/lib/use-button-pending-action";
import { extractAvatarVariant } from "src/lib/utils/avatar-utils";
import { Drawer } from "vaul";
import { CTA_CLICKED_VALUES } from "@repo/statsig";

interface BlockVisitModalContentProps {
  patientData: {
    id: string;
    firstName: string;
    profileImage?: string;
  };
  onDismiss?: () => void;
}

const BlockVisitModalContent: React.FC<BlockVisitModalContentProps> = ({
  patientData,
  onDismiss,
}) => {
  const router = useRouter();
  const { isPending, startAction } = useButtonPendingAction();

  const handleDismiss = () => {
    startAction(async () => {
      if (onDismiss) {
        onDismiss();
      } else {
        router.push(Routes.HOME);
      }
    });
  };

  // Use the utility function to extract and validate the avatar variant
  const avatarVariant = extractAvatarVariant(
    patientData.profileImage,
    patientData.id
  );

  return (
    <div className="p-6">
      <Drawer.Title className="sr-only">
        {patientData.firstName} has an active visit
      </Drawer.Title>
      <Drawer.Description className="sr-only">
        Please complete the current visit before creating a new one
      </Drawer.Description>

      <div className="flex flex-col items-center text-center">
        {/* Avatar */}
        <div className="mb-4">
          <Avatar variant={avatarVariant} size="modal" />
        </div>

        {/* Title - Center aligned */}
        <HeadlineText className="mb-3 text-center">
          {patientData.firstName} has an active visit
        </HeadlineText>

        {/* Body Text */}
        <BodyText className="mb-6 text-left w-full">
          Please complete the current visit before creating a new one. If there
          are new symptoms, message your provider in the active thread.
        </BodyText>

        {/* Secondary Button */}
        <BrandedActionButton
          onPress={handleDismiss}
          text="DISMISS"
          eventData={{
            action: CTA_CLICKED_VALUES.DISMISS_BLOCKED_VISIT,
            buttonText: "DISMISS",
            patient_id: patientData.id,
            patient_name: patientData.firstName,
          }}
          disabled={isPending}
          loading={isPending}
          variant="secondary"
          className="m-0 w-full"
        />
      </div>
    </div>
  );
};

export default BlockVisitModalContent;
